import React, { useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import startCase from 'lodash/startCase';
import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem } from 'design';
import { routeConstants } from 'routes';
import { logOutRequest, selectCurrentEmployee, switchProfile } from 'store/App';
import { ICurrentEmployee, IEmployerState, IUserState } from 'store/types';
import { EmployeeType, IEmployerSite, SiteRole } from 'types';
import { Avatar } from '../Avatar';
import { Icon } from '../Icon';
import { HeaderDropdown } from './HeaderDropdown';

interface IHeaderProfileSwitcherProps {
  user: IUserState;
  employers: Record<string, IEmployerState>;
  isLoading: boolean;
  className?: string;
  isNavOpen?: boolean;
  onNavOpenChange?: () => void;
}

const Layout = styled.button<{ isOpen: boolean }>`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: auto;
  height: ${toRem(44)};
  border-radius: ${toRem(11)};
  transition: background-color ${theme.transition.button.fast};

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: ${toRem(theme.space.xs)} ${toRem(theme.space.lg)} ${toRem(theme.space.xs)} ${toRem(theme.space.xs)};

    &:hover {
      background-color: rgba(255, 255, 255, 0.055);
    }
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      background-color: rgba(255, 255, 255, 0.055);
    `}
`;

const User = styled.div`
  position: relative;
  flex-grow: 1;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${toRem(theme.space.md)};
`;

const UserInfo = styled.div`
  display: flex;
  position: relative;
  top: ${toRem(1.25)};
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  height: 100%;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  color: ${theme.color.white};
  text-align: left;
`;

const Name = styled.div`
  position: relative;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
`;

const ActiveProfile = styled.div`
  margin-top: ${toRem(theme.space.xs)};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.xxs)};
  white-space: nowrap;
`;

const EmployerName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${theme.breakpoint.med}px) {
    max-width: ${toRem(350)};
  }

  @media (max-width: ${theme.breakpoint.small}px) {
    max-width: ${toRem(200)};
  }
`;

const IconCarrot = styled(Icon).attrs({ icon: 'Carrot', width: 6, height: 4, fill: theme.color.neutral[400] })`
  position: relative;
  top: ${toRem(-1)};
  margin-left: ${toRem(theme.space.sm)};
`;

const addressForSite = (site: IEmployerSite): string | undefined => site.address?.address1 && site.address?.city && `${site.address.address1}, ${site.address.city} ${site.address.state} ${site.address.zipCode}`;

const getActiveProfileText = (currentEmployerOrg?: IEmployerState, currentEmployerSite?: IEmployerSite) => {
  if (currentEmployerOrg) {
    if (currentEmployerOrg.type === EmployeeType.ADMIN) {
      return (
        <>
          <EmployerName>{startCase(currentEmployerOrg.name)}</EmployerName>, Admin
        </>
      );
    }
    if (currentEmployerSite && currentEmployerOrg.type === EmployeeType.USER) {
      return (
        <>
          <EmployerName>{startCase(currentEmployerSite.name)}</EmployerName>, {startCase(currentEmployerSite.role)}
        </>
      );
    }
  }
  return '';
};

const HeaderProfileSwitcher: React.FC<IHeaderProfileSwitcherProps> = ({ isLoading, user, employers, className, isNavOpen, onNavOpenChange }: IHeaderProfileSwitcherProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentEmployerOrg = employers?.[currentEmployee?.organizationId]; // Current Employee Organization
  const currentEmployerSite = currentEmployerOrg?.sites?.[currentEmployee?.siteId]; // Current Employee Site
  const [isOpen, setIsOpen] = useState(false);

  const handleProfileSwitcherChange = () => {
    setIsOpen(!isOpen);

    if (isNavOpen && onNavOpenChange) onNavOpenChange();
  };

  const activeProfileText = getActiveProfileText(currentEmployerOrg, currentEmployerSite);

  // Build admin roles
  const adminRoleOrgs = useMemo(() => (employers ? Object.values(employers).filter((org) => org.type === EmployeeType.ADMIN) : []), [employers]);

  // Build management roles
  const managerRoleSites = useMemo(() => {
    const sites: any = [];
    const organizations = employers ? Object.values(employers).filter((org) => org.type === EmployeeType.USER) : [];

    organizations.forEach((org) =>
      Object.values(org.sites).forEach((site) => {
        if (site.role === SiteRole.ADMIN || site.role === SiteRole.MANAGER) {
          sites.push({ ...site, organizationId: org.id, address: addressForSite(site) });
        }
      })
    );

    return sites;
  }, [employers]);

  const handleNavigateToSettings = () => {
    setIsOpen(false);
    history.push(routeConstants.private.settingsAccount);
  };

  const handleOnSelect = (option: ICurrentEmployee) => {
    dispatch(switchProfile(option));

    // Redirect to root (but not if on Add DOB)
    if (history.location.pathname !== routeConstants.private.addDOB) history.push(routeConstants.private.rootPath);
  };

  const handleSelectSelf = () => {
    dispatch(switchProfile());
    history.push(routeConstants.private.rootPath);
  };

  return (
    <>
      <Layout isOpen={isOpen} className={className} onClick={handleProfileSwitcherChange} data-cy="header-profile-switcher">
        <User>
          <Avatar size="small" username={`${user?.firstName} ${user?.lastName}`} image={user?.avatarUrl} isLoading={isLoading} />
          {isLoading ? (
            <Skeleton width={100} />
          ) : (
            <UserInfo>
              <Name>
                {`${user?.firstName} ${user?.lastName}`}
                <IconCarrot />
              </Name>
              {activeProfileText && <ActiveProfile data-cy="active-profile">{activeProfileText}</ActiveProfile>}
            </UserInfo>
          )}
        </User>
      </Layout>
      <HeaderDropdown
        adminRoleOrgs={adminRoleOrgs}
        managerRoleOrgs={managerRoleSites}
        currentOption={currentEmployee}
        username={`${user?.firstName} ${user?.lastName}`}
        isVisible={isOpen}
        onClose={() => setIsOpen(!isOpen)}
        onSelectSelf={handleSelectSelf}
        onSelect={handleOnSelect}
        onSettings={handleNavigateToSettings}
        onLogout={() => dispatch(logOutRequest(`${routeConstants.public.login}?message=logged_out`))}
      />
    </>
  );
};

export default HeaderProfileSwitcher;
